import React, { useRef, useState, useCallback, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import cn from "classnames";
import copy from "copy-to-clipboard";

import Label from "../Label";
import Tooltip from "../Tooltip";
import Validation from "../Validation";

import { FormElement, Copied, ChangeButton } from "../shared-style";
import { StyledInput } from "./styles";
import messages from "./messages";
import { IProps } from "./interfaces";

const InputText = ({
    label,
    value,
    onChange,
    disabled,
    name,
    changeModal,
    validation,
    lightWithValue,
    onBlur,
    placeholder,
    required,
    email,
    tooltip,
    selectAllOnClick,
    selectAllOnMount,
    focusOnMount,
    copyOnClick,
    maxLength,
    id,
    onFocus,
}: IProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [copied, setCopied] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout>>();
    useEffect(() => {
        if (selectAllOnMount) {
            inputRef!.current!.focus();
            const type = inputRef!.current!.type;
            inputRef!.current!.type = "text";
            inputRef!.current!.setSelectionRange(0, value.length);
            inputRef!.current!.type = type;
        } else if (focusOnMount) {
            inputRef!.current!.focus();
        }
        return () => {
            clearTimeout(timerRef!.current!);
        }
    }, [])

    const onClick = useCallback(() => {
        if (selectAllOnClick) {
            inputRef!.current!.focus();
            inputRef!.current!.setSelectionRange(0, value.length);
        }
        if (copyOnClick) {
            copy(value);
            setCopied(true);
            timerRef.current = setTimeout(() => {
                setCopied(false);
            }, 2500);
        }
    }, [selectAllOnClick, value, copyOnClick, inputRef, setCopied, timerRef]);

    return (
        <FormElement>
            {label && (
                <Label disabled={disabled} required={required}>
                    {label}
                    {copied && (
                        <Copied>
                            <FormattedMessage {...messages.copied} />
                        </Copied>
                    )}
                </Label>
            )}
            {validation && <Validation validation={validation} />}
            {changeModal && (
                <ChangeButton onClick={() => changeModal(name!, value)}>
                    <FormattedMessage {...messages.modify} />
                </ChangeButton>
            )}
            <StyledInput
                className={cn(validation && "ValidationState", lightWithValue && value !== "" && "ElementLight")}
                type={email ? "email" : "text"}
                value={value}
                onChange={onChange}
                ref={inputRef}
                disabled={disabled}
                name={name}
                maxLength={maxLength}
                onFocus={onFocus}
                onBlur={onBlur}
                onClick={onClick}
                placeholder={placeholder || ""}
                id={id}
            />
            {tooltip && <Tooltip>{tooltip}</Tooltip>}
        </FormElement>
    );
}

export default InputText;
